import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useApp } from '@/context/AppContext';
import { useData } from '@/context/DataContext';
import { formatEntityLabel } from '@/lib/tracking';
import { FEED_INVALIDATION_EVENT } from '@/lib/feed-sync';
import { ArticleDrawer } from '@/components/feed/ArticleDrawer';

type Section = 'all' | 'news' | 'research' | 'newsletters' | 'podcasts';
type FeedMode = 'top' | 'latest';

const SECTIONS: Array<{ id: Section; label: string; icon: string }> = [
  { id: 'all', label: 'All', icon: '\u25C9' },
  { id: 'news', label: 'News', icon: '\u25B2' },
  { id: 'research', label: 'Research', icon: '\u25C6' },
  { id: 'newsletters', label: 'Letters', icon: '\u25A3' },
  { id: 'podcasts', label: 'Pods', icon: '\u25CF' },
];

interface FeedItem {
  id: string;
  section: string;
  content_type: string;
  title: string;
  summary: string;
  url: string;
  published_at: string;
  feed_name: string;
  feed_category: string;
  relevance: number | null;
  topics: string[] | null;
  entities: Array<{
    id: string;
    name: string;
    kind: string;
    ticker?: string | null;
  }> | null;
  verdict: string | null;
  thesis: string | null;
  sentiment: string | null;
  score: number;
  source_count: number;
}

const SECTION_ACCENT: Record<string, string> = {
  news: '#58a6ff',
  research: '#a371f7',
  newsletters: '#3fb950',
  podcasts: '#d29922',
};

const MAX_VISIBLE_FEED_ITEM_AGE_MS = 14 * 24 * 60 * 60 * 1000;

const TOPIC_DISPLAY: Record<string, string> = {
  ai: 'AI',
  saas: 'SaaS',
  fundraising: 'Fundraising',
  'm&a': 'M&A',
  ipo: 'IPO',
  infrastructure: 'Infrastructure',
  semiconductors: 'Semiconductors',
  'open source': 'Open Source',
  regulation: 'Regulation',
  security: 'Security',
  crypto: 'Crypto',
  robotics: 'Robotics',
  'defence tech': 'Defence Tech',
  fintech: 'Fintech',
  'climate tech': 'Climate Tech',
  enterprise: 'Enterprise',
  'developer tools': 'Developer Tools',
  biotech: 'Biotech',
  hardware: 'Hardware',
  'consumer tech': 'Consumer Tech',
};

function displayTopic(topic: string): string {
  const normalized = topic.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');

  if (TOPIC_DISPLAY[normalized]) {
    return TOPIC_DISPLAY[normalized];
  }

  const ACRONYMS: Record<string, string> = {
    ai: 'AI',
    ipo: 'IPO',
    saas: 'SaaS',
    'm&a': 'M&A',
    ml: 'ML',
    nlp: 'NLP',
  };

  return normalized
    .split(' ')
    .filter(Boolean)
    .map((word) => ACRONYMS[word] ?? word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function displayVerdict(verdict: string | null): string | null {
  if (!verdict) return null;
  if (verdict === 'must_read') return 'Must Read';
  if (verdict === 'worth_reading') return 'Worth Reading';
  if (verdict === 'skim') return 'Skim';
  if (verdict === 'skip') return 'Skip';
  return verdict;
}

function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&#(\d+);/g, (_, value: string) => {
      const code = parseInt(value, 10);
      return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : '';
    })
    .replace(/&#x([0-9a-fA-F]+);/g, (_, value: string) => {
      const code = parseInt(value, 16);
      return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : '';
    })
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lsquo;/g, '\u2018')
    .replace(/&rsquo;/g, '\u2019')
    .replace(/&ldquo;/g, '\u201C')
    .replace(/&rdquo;/g, '\u201D')
    .replace(/&hellip;/g, '\u2026');
}

function stripFeedBoilerplate(text: string): string {
  return text
    .replace(/\bArticle URL:\s*\S+/gi, ' ')
    .replace(/\bComments URL:\s*\S+/gi, ' ')
    .replace(/\bPoints:\s*\d+/gi, ' ')
    .replace(/\b#\s*Comments:\s*\d+/gi, ' ')
    .replace(/\b\d+\s+comments?\b/gi, ' ')
    .replace(/\s*[|•·]\s*/g, ' ');
}

function normalizeFeedText(text: string | null | undefined): string {
  if (!text) return '';

  let cleaned = decodeHtmlEntities(text);
  cleaned = decodeHtmlEntities(cleaned);
  cleaned = stripFeedBoilerplate(cleaned);

  return cleaned.replace(/\s+/g, ' ').trim();
}

function SaveIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  );
}

function NoteIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ThumbUpIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M7 10v12M15 5.88L14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
    </svg>
  );
}

function ThumbDownIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 14V2M9 18.12L10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L14 22a3.13 3.13 0 0 1-3-3.88Z" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="7" cy="7" r="5" />
      <path d="M11 11l3.5 3.5" />
    </svg>
  );
}

interface SearchResult {
  id: string;
  section: string;
  content_type: string;
  title: string;
  summary: string;
  url: string;
  published_at: string;
  feed_name: string;
  feed_category: string;
  relevance: number | null;
  topics: string[] | null;
  entities: Array<{
    id: string;
    name: string;
    kind: string;
    ticker?: string | null;
  }> | null;
  verdict: string | null;
  thesis: string | null;
  sentiment: string | null;
  source_count: number;
  search_rank: number;
}

export function FeedPage() {
  const { supabaseClient, isSupabaseReady } = useAuth();
  const { openModal, modal } = useApp();
  const { items: piItems } = useData();
  const navigate = useNavigate();

  const piLinkUrls = useMemo(() => new Set(piItems.links.map((l) => l.url)), [piItems.links]);

  // Resolve the Supabase user ID (needed for user_actions table queries)
  const [supabaseUserId, setSupabaseUserId] = useState<string | null>(null);

  useEffect(() => {
    if (!supabaseClient || !isSupabaseReady) return;
    void supabaseClient.auth.getSession().then(({ data: { session } }) => {
      setSupabaseUserId(session?.user?.id ?? null);
    });
  }, [supabaseClient, isSupabaseReady]);

  const [section, setSection] = useState<Section>('all');
  const [mode, setMode] = useState<FeedMode>('top');
  const [items, setItems] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showCount, setShowCount] = useState(30);
  const [refreshToken, setRefreshToken] = useState(0);
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const isSearchActive = searchQuery.length > 0;
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<FeedItem[]>([]);
  const activeRequestIdRef = useRef(0);
  const lastLoadedViewKeyRef = useRef<string | null>(null);
  const searchContextRef = useRef({
    userId: supabaseUserId,
    section: 'all' as Section,
  });

  // Supabase session timeout — show loading skeleton while waiting, error after 8s
  const [supabaseTimeout, setSupabaseTimeout] = useState(false);

  useEffect(() => {
    if (isSupabaseReady) {
      setSupabaseTimeout(false);
      return;
    }
    const timer = setTimeout(() => setSupabaseTimeout(true), 8000);
    return () => clearTimeout(timer);
  }, [isSupabaseReady]);

  // Track saved, hidden, and read item IDs locally for instant UI updates
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [hidingIds, setHidingIds] = useState<Set<string>>(new Set());
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [readFilter, setReadFilter] = useState<'all' | 'unread'>('all');
  const [previewItem, setPreviewItem] = useState<FeedItem | null>(null);

  // Undo-hide state: shows overlay for 3s before actually hiding
  const [undoHideId, setUndoHideId] = useState<string | null>(null);
  const undoTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Mark-all-read undo state: delays Supabase write for 5s, allows undo
  const [pendingMarkAllRead, setPendingMarkAllRead] = useState<{
    ids: string[];
    timeoutId: ReturnType<typeof setTimeout>;
  } | null>(null);
  const pendingMarkAllReadRef = useRef(pendingMarkAllRead);
  pendingMarkAllReadRef.current = pendingMarkAllRead;

  // Paywalled feeds for lock icon
  const [paywalledFeeds, setPaywalledFeeds] = useState<Set<string>>(new Set());

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const loadFeed = useCallback(async () => {
    if (!supabaseClient || !supabaseUserId) return;
    const currentViewKey = `${supabaseUserId}:${mode}:${section}`;
    const isViewChange = lastLoadedViewKeyRef.current !== currentViewKey;
    const isInitialLoad = itemsRef.current.length === 0 || isViewChange;
    const requestId = activeRequestIdRef.current + 1;

    activeRequestIdRef.current = requestId;

    if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    const { data, error } = await supabaseClient.rpc('get_user_feed', {
      p_mode: mode,
      p_section: section,
      p_limit: 200,
      p_offset: 0,
    });

    if (activeRequestIdRef.current !== requestId) {
      return;
    }

    if (error) {
      console.error('Feed RPC error:', error);
      if (isInitialLoad) {
        setItems([]);
      }
    } else {
      setItems((data ?? []) as FeedItem[]);
      lastLoadedViewKeyRef.current = currentViewKey;
    }
    setLoading(false);
    setRefreshing(false);
  }, [supabaseClient, supabaseUserId, section, mode]);

  // Load existing user actions (saved/hidden/read) on mount
  useEffect(() => {
    if (!supabaseClient || !supabaseUserId) return;
    async function loadActions() {
      const { data } = await supabaseClient!
        .from('user_actions')
        .select('content_id, action')
        .eq('user_id', supabaseUserId!)
        .in('action', ['save', 'hide', 'read']);

      if (data) {
        const saved = new Set<string>();
        const hidden = new Set<string>();
        const read = new Set<string>();
        for (const row of data as Array<{ content_id: string; action: string }>) {
          if (row.action === 'save') saved.add(row.content_id);
          if (row.action === 'hide') hidden.add(row.content_id);
          if (row.action === 'read') read.add(row.content_id);
        }
        setSavedIds(saved);
        setHiddenIds(hidden);
        setReadIds(read);
      }
    }
    void loadActions();
  }, [supabaseClient, supabaseUserId]);

  // Load paywalled feed names on mount
  useEffect(() => {
    if (!supabaseClient || !supabaseUserId) return;
    async function loadPaywalled() {
      const { data } = await supabaseClient!.from('feeds').select('name').eq('paywalled', true);
      if (data) setPaywalledFeeds(new Set(data.map((f: { name: string }) => f.name)));
    }
    void loadPaywalled();
  }, [supabaseClient, supabaseUserId]);

  useEffect(() => {
    searchContextRef.current = {
      userId: supabaseUserId,
      section,
    };
  }, [supabaseUserId, section]);

  useEffect(() => {
    void loadFeed();
  }, [loadFeed, refreshToken]);

  useEffect(() => {
    function handleFeedInvalidation() {
      setRefreshToken((token) => token + 1);
    }

    window.addEventListener(FEED_INVALIDATION_EVENT, handleFeedInvalidation);
    return () => window.removeEventListener(FEED_INVALIDATION_EVENT, handleFeedInvalidation);
  }, []);

  // Re-fetch feed data periodically (every 90s when tab is visible)
  useEffect(() => {
    const loadOnVisibility = () => {
      if (document.visibilityState === 'visible' && !modal.isOpen) {
        void loadFeed();
      }
    };

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible' && !isSearchActive && !modal.isOpen) {
        void loadFeed();
      }
    }, 90_000);

    document.addEventListener('visibilitychange', loadOnVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', loadOnVisibility);
    };
  }, [loadFeed, isSearchActive, modal.isOpen]);

  // Execute search against Supabase RPC
  const executeSearch = useCallback(
    async (query: string) => {
      const trimmedQuery = query.trim();
      const { userId, section: activeSection } = searchContextRef.current;

      if (!userId || !trimmedQuery || !supabaseClient) {
        setSearchResults([]);
        return;
      }
      setSearchLoading(true);

      const { data, error } = await supabaseClient.rpc('search_content', {
        p_query: trimmedQuery,
        p_section: activeSection,
        p_limit: 50,
      });

      if (error) {
        console.error('Search RPC error:', error);
        setSearchResults([]);
      } else {
        setSearchResults((data ?? []) as SearchResult[]);
      }
      setSearchLoading(false);
    },
    [supabaseClient]
  );

  // Debounced search on input change
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!searchInput.trim()) {
      setSearchQuery('');
      setSearchResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      const nextQuery = searchInput.trim();
      setSearchQuery(nextQuery);
      void executeSearch(nextQuery);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput, executeSearch]);

  useEffect(() => {
    if (searchQuery) {
      void executeSearch(searchQuery);
    }
  }, [section, refreshToken, searchQuery, executeSearch]);

  function clearSearch() {
    setSearchInput('');
    setSearchQuery('');
    setSearchResults([]);
  }

  // Map search results to the same shape as feed items for rendering
  const searchDisplayItems: FeedItem[] = useMemo(
    () =>
      searchResults.map((r) => ({
        id: r.id,
        section: r.section,
        content_type: r.content_type,
        title: r.title,
        summary: r.summary,
        url: r.url,
        published_at: r.published_at,
        feed_name: r.feed_name,
        feed_category: r.feed_category,
        relevance: r.relevance,
        topics: r.topics,
        entities: r.entities,
        verdict: r.verdict,
        thesis: r.thesis,
        sentiment: r.sentiment,
        score: r.search_rank,
        source_count: r.source_count,
      })),
    [searchResults]
  );

  async function handleSave(contentId: string) {
    if (!supabaseClient || !supabaseUserId) return;
    const alreadySaved = savedIds.has(contentId);

    // Optimistic update
    setSavedIds((prev) => {
      const next = new Set(prev);
      if (alreadySaved) {
        next.delete(contentId);
      } else {
        next.add(contentId);
      }
      return next;
    });

    if (alreadySaved) {
      // Remove the save action
      await supabaseClient
        .from('user_actions')
        .delete()
        .eq('user_id', supabaseUserId)
        .eq('content_id', contentId)
        .eq('action', 'save');
    } else {
      // Upsert a save action
      await supabaseClient
        .from('user_actions')
        .upsert(
          { user_id: supabaseUserId, content_id: contentId, action: 'save' },
          { onConflict: 'user_id,content_id,action' }
        );
    }
  }

  async function handleMarkRead(contentId: string) {
    if (!supabaseClient || !supabaseUserId) return;
    if (readIds.has(contentId)) return;

    // Optimistic update
    setReadIds((prev) => new Set(prev).add(contentId));

    await supabaseClient
      .from('user_actions')
      .upsert(
        { user_id: supabaseUserId, content_id: contentId, action: 'read' },
        { onConflict: 'user_id,content_id,action' }
      );
  }

  function handleMarkAllRead() {
    if (!supabaseClient || !supabaseUserId) return;

    // If there's a pending mark-all-read, commit it immediately first
    if (pendingMarkAllReadRef.current) {
      clearTimeout(pendingMarkAllReadRef.current.timeoutId);
      commitMarkAllRead(pendingMarkAllReadRef.current.ids);
    }

    // Use displayItems up to showCount — what the user has actually scrolled to
    const targetItems = displayItems.slice(0, showCount).filter((i) => !readIds.has(i.id));
    if (targetItems.length === 0) return;

    const ids = targetItems.map((i) => i.id);

    // Optimistic update
    setReadIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });

    // Delay Supabase write for 5 seconds (undo window)
    const timeoutId = setTimeout(() => {
      commitMarkAllRead(ids);
    }, 5000);

    setPendingMarkAllRead({ ids, timeoutId });
  }

  function commitMarkAllRead(ids: string[]) {
    if (!supabaseClient || !supabaseUserId) return;

    const rows = ids.map((contentId) => ({
      user_id: supabaseUserId,
      content_id: contentId,
      action: 'read' as const,
    }));

    void supabaseClient
      .from('user_actions')
      .upsert(rows, { onConflict: 'user_id,content_id,action' })
      .then(({ error }) => {
        if (error) {
          // Roll back on error
          setReadIds((prev) => {
            const next = new Set(prev);
            for (const id of ids) next.delete(id);
            return next;
          });
          console.error('Mark all read failed:', error);
        }
      });

    setPendingMarkAllRead(null);
  }

  function undoMarkAllRead() {
    if (!pendingMarkAllRead) return;

    clearTimeout(pendingMarkAllRead.timeoutId);
    const { ids } = pendingMarkAllRead;

    // Remove from local read state
    setReadIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.delete(id);
      return next;
    });

    setPendingMarkAllRead(null);
  }

  function commitHide(contentId: string) {
    // Start fade-out animation
    setHidingIds((prev) => new Set(prev).add(contentId));

    // After animation completes, mark as hidden
    setTimeout(() => {
      setHidingIds((prev) => {
        const next = new Set(prev);
        next.delete(contentId);
        return next;
      });
      setHiddenIds((prev) => new Set(prev).add(contentId));
      setUndoHideId((current) => (current === contentId ? null : current));
    }, 300);

    // Write to Supabase
    if (supabaseClient && supabaseUserId) {
      void supabaseClient
        .from('user_actions')
        .upsert(
          { user_id: supabaseUserId, content_id: contentId, action: 'hide' },
          { onConflict: 'user_id,content_id,action' }
        );
    }
  }

  function handleHide(contentId: string) {
    if (!supabaseClient || !supabaseUserId) return;

    // If another item had a pending undo, commit it immediately
    if (undoHideId && undoHideId !== contentId) {
      clearTimeout(undoTimeoutRef.current);
      commitHide(undoHideId);
    }

    // Show undo overlay on this item
    setUndoHideId(contentId);

    // After 3 seconds, actually hide
    clearTimeout(undoTimeoutRef.current);
    undoTimeoutRef.current = setTimeout(() => {
      commitHide(contentId);
    }, 3000);
  }

  function timeAgo(dateStr: string): string {
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return 'now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
  }

  function isWithinVisibleAge(dateStr: string): boolean {
    const ageMs = Date.now() - new Date(dateStr).getTime();
    return ageMs <= MAX_VISIBLE_FEED_ITEM_AGE_MS;
  }

  // Filter out hidden items
  const visibleItems = items.filter((item) => {
    if (hiddenIds.has(item.id)) return false;
    return isWithinVisibleAge(item.published_at);
  });
  // Choose which items to display: search results or normal feed, apply read filter
  const baseDisplayItems = (isSearchActive ? searchDisplayItems : visibleItems)
    .filter((item) => !hiddenIds.has(item.id) && isWithinVisibleAge(item.published_at))
    .filter((item) => readFilter === 'all' || !readIds.has(item.id));
  const displayItems = baseDisplayItems;
  const isLoading = isSearchActive ? searchLoading : loading;

  // Infinite scroll: auto-load more items when sentinel enters viewport
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && displayItems.length > showCount) {
          setShowCount((prev) => prev + 30);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [displayItems.length, showCount]);

  // Feed Unavailable fallback — show skeleton while Supabase session initializes, error after timeout
  if (!isSupabaseReady || !supabaseClient) {
    if (supabaseTimeout) {
      return (
        <div className="page-shell">
          <div className="empty-state-panel">
            <p className="type-title" style={{ color: 'var(--text-primary)' }}>
              Feed Unavailable
            </p>
            <p className="type-body" style={{ color: 'var(--text-secondary)' }}>
              Feed features require additional setup. Please sign out and sign back in.
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="page-shell">
        <div className="page-header">
          <div>
            <div className="page-eyebrow">Daily briefing</div>
            <h1 className="page-title">Feed</h1>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className={`skeleton rounded-lg ${i === 0 ? 'md:col-span-2' : ''}`}
              style={{ height: i === 0 ? '160px' : '120px' }}
            />
          ))}
        </div>
      </div>
    );
  }

  // Save a feed item as a personal Link via the AddEdit modal
  function saveToPersonalInsights(item: FeedItem) {
    const safeUrl = /^https?:\/\//i.test(item.url) ? item.url : '';
    if (!safeUrl) return;
    if (piLinkUrls.has(safeUrl)) return;
    openModal({
      mode: 'add',
      itemType: 'link',
      prefillUrl: safeUrl,
      prefillTitle: normalizeFeedText(item.title),
      prefillTags: ['inbox', 'from-feed'],
    });
  }

  // Open a new Note pre-filled with the feed item reference
  function addNoteFromFeed(item: FeedItem) {
    const title = normalizeFeedText(item.title);
    const safeUrl = /^https?:\/\//i.test(item.url) ? item.url : '';
    // Escape HTML entities to prevent XSS from malicious feed data
    const esc = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const content = safeUrl
      ? `<p><strong>Re:</strong> <a href="${esc(safeUrl)}" target="_blank">${esc(title)}</a> (${esc(item.feed_name)})</p><p></p>`
      : `<p><strong>Re:</strong> ${esc(title)} (${esc(item.feed_name)})</p><p></p>`;
    openModal({
      mode: 'add',
      itemType: 'note',
      prefillTitle: `Notes on: ${title}`,
      prefillContent: content,
      prefillTags: ['inbox', 'from-feed'],
    });
  }

  // Render a single tile card
  function renderTile(item: FeedItem, i: number, isLead: boolean) {
    const isSkim = item.verdict === 'skim';
    const isRead = readIds.has(item.id);
    const accent = SECTION_ACCENT[item.section] ?? 'var(--accent-primary)';
    const isSaved = savedIds.has(item.id);
    const isHiding = hidingIds.has(item.id);
    const isPaywalled = paywalledFeeds.has(item.feed_name);
    const visibleTopics = item.topics ?? [];
    const visibleEntities = item.entities ?? [];
    const verdictLabel = displayVerdict(item.verdict);
    const displayTitle = normalizeFeedText(item.title);
    const displaySummary = normalizeFeedText(item.summary);
    const displayThesis = normalizeFeedText(item.thesis);

    return (
      <div
        key={item.id}
        className={`group relative tile-card animate-fade-in-up ${isLead ? 'tile-lead' : ''}`}
        data-skim={isSkim || undefined}
        data-read={isRead || undefined}
        style={{
          animationDelay: i < 10 ? `${i * 20}ms` : '0ms',
          animationFillMode: i < 10 ? 'both' : undefined,
          opacity: isHiding ? 0 : undefined,
          transform: isHiding ? 'scale(0.97) translateY(-4px)' : undefined,
          transition: 'opacity 300ms ease-out, transform 300ms ease-out',
        }}
      >
        <a
          href={/^https?:\/\//i.test(item.url) ? item.url : '#'}
          className="block"
          onClick={(e) => {
            e.preventDefault();
            void handleMarkRead(item.id);
            setPreviewItem(item);
          }}
        >
          {/* Meta row */}
          <div
            className={`flex items-center gap-x-2 gap-y-1 flex-wrap ${isLead ? 'mb-3' : 'mb-2'}`}
          >
            <span className="feed-kicker" style={{ color: accent }}>
              {item.section}
            </span>
            {verdictLabel && (
              <span
                className="feed-kicker px-1.5 py-0.5 rounded-full"
                style={{
                  background: 'rgba(35, 131, 226, 0.12)',
                  color: 'var(--accent-primary)',
                }}
              >
                {verdictLabel}
              </span>
            )}
            <span className="type-caption" style={{ color: 'var(--text-tertiary)' }}>
              &middot;
            </span>
            <span className="type-caption truncate" style={{ color: 'var(--text-tertiary)' }}>
              {item.feed_name}
            </span>
            {isPaywalled && (
              <span
                className="type-caption"
                style={{ color: 'var(--warning)' }}
                title="Paid source"
              >
                {'\uD83D\uDD12'}
              </span>
            )}
            <span className="type-caption" style={{ color: 'var(--text-tertiary)' }}>
              &middot;
            </span>
            <span className="type-caption" style={{ color: 'var(--text-tertiary)' }}>
              {timeAgo(item.published_at)}
            </span>
            {item.source_count > 1 && (
              <span
                className="feed-kicker px-1.5 py-0.5 rounded-full"
                style={{
                  background: 'var(--bg-tertiary)',
                  color: 'var(--text-secondary)',
                }}
              >
                {item.source_count} sources
              </span>
            )}
            {isRead && (
              <span
                className="feed-kicker px-1.5 py-0.5 rounded-full"
                style={{
                  background: 'rgba(0, 0, 0, 0.06)',
                  color: 'var(--text-tertiary)',
                  fontSize: '10px',
                }}
              >
                &#x2713; Read
              </span>
            )}
          </div>
          {/* Title */}
          <h3
            className={`${isLead ? 'type-hero' : 'type-headline'} mb-1.5 group-hover:underline decoration-1 underline-offset-2`}
            style={{ color: isRead ? 'var(--text-tertiary)' : 'var(--text-primary)' }}
          >
            {displayTitle}
          </h3>
          {/* Summary / Paywall distinction */}
          {isPaywalled ? (
            <p className="type-body mb-2" style={{ color: 'var(--text-tertiary)' }}>
              Read at source &rarr;
            </p>
          ) : displaySummary ? (
            <p
              className={`type-body ${isLead ? 'line-clamp-3' : 'line-clamp-2'} mb-2`}
              style={{ color: 'var(--text-secondary)' }}
            >
              {displaySummary}
            </p>
          ) : null}
          {isLead && displayThesis && (
            <p className="type-body italic mb-2" style={{ color: 'var(--text-tertiary)' }}>
              {displayThesis}
            </p>
          )}
          {(visibleTopics.length > 0 || visibleEntities.length > 0) && (
            <div className={`flex gap-1.5 flex-wrap ${isLead ? 'mt-3' : 'mt-2'}`}>
              {visibleTopics.slice(0, isLead ? 5 : 3).map((topic) => (
                <span key={topic} className="feed-chip">
                  {displayTopic(topic)}
                </span>
              ))}
              {visibleEntities.slice(0, isLead ? 3 : 2).map((entity) => (
                <span key={entity.id} className="feed-chip feed-chip-entity">
                  {formatEntityLabel(entity)}
                </span>
              ))}
            </div>
          )}
        </a>
        {/* Action overlay */}
        <div className="absolute top-2 right-2 flex items-center gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-150">
          {!isRead && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void handleMarkRead(item.id);
              }}
              className="thumb-btn"
              title="Mark as read"
            >
              <CheckIcon />
            </button>
          )}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              saveToPersonalInsights(item);
            }}
            className="thumb-btn"
            style={
              piLinkUrls.has(item.url)
                ? {
                    color: 'var(--accent-primary)',
                    borderColor: 'var(--accent-primary)',
                    background: 'var(--accent-secondary)',
                  }
                : undefined
            }
            title={piLinkUrls.has(item.url) ? 'Already saved' : 'Save as link'}
          >
            <SaveIcon />
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              addNoteFromFeed(item);
            }}
            className="thumb-btn hidden md:flex"
            title="Add note about this"
          >
            <NoteIcon />
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void handleSave(item.id);
            }}
            className={`thumb-btn thumb-up hidden md:flex ${isSaved ? 'thumb-active-up' : ''}`}
            title="More like this"
          >
            <ThumbUpIcon />
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleHide(item.id);
            }}
            className="thumb-btn thumb-down"
            title="Less like this"
          >
            <ThumbDownIcon />
          </button>
        </div>
        {/* Undo-hide overlay */}
        {undoHideId === item.id && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center rounded-lg"
            style={{ background: 'var(--bg-tertiary)' }}
          >
            <span className="type-label" style={{ color: 'var(--text-secondary)' }}>
              Hidden
            </span>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                clearTimeout(undoTimeoutRef.current);
                setUndoHideId(null);
              }}
              className="ml-3 type-label"
              style={{
                color: 'var(--accent-primary)',
                cursor: 'pointer',
                background: 'none',
                border: 'none',
              }}
            >
              Undo
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Daily briefing</div>
          <h1 className="page-title">Feed</h1>
          <p className="page-subtitle">
            A daily view of the sources, topics, and companies you follow.
          </p>
        </div>
        {!isSearchActive && (
          <div className="page-metrics">
            <span className="metric-chip type-caption">
              <strong>{mode === 'top' ? 'Top' : 'Latest'}</strong>
              view
            </span>
            {refreshing && <span className="metric-chip type-caption">Refreshing</span>}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-col gap-3 mb-4 md:flex-row md:items-center md:justify-between md:mb-6">
        {/* Section pills + read filter */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Section pills -- scrollable on mobile */}
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-0.5">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setSection(s.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md type-label transition-notion whitespace-nowrap shrink-0"
                style={{
                  color: section === s.id ? 'var(--text-primary)' : 'var(--text-tertiary)',
                  background: section === s.id ? 'var(--bg-tertiary)' : 'transparent',
                }}
              >
                <span
                  style={{
                    fontSize: '10px',
                    color: SECTION_ACCENT[s.id] ?? 'var(--accent-primary)',
                  }}
                >
                  {s.icon}
                </span>
                {s.label}
              </button>
            ))}
          </div>
          {/* Read filter toggle */}
          <span
            className="hidden md:inline"
            style={{ color: 'var(--border-primary)', margin: '0 2px' }}
          >
            |
          </span>
          <button
            onClick={() => setReadFilter(readFilter === 'all' ? 'unread' : 'all')}
            className="px-2.5 py-1.5 rounded-md type-label transition-notion whitespace-nowrap shrink-0"
            style={{
              color: readFilter === 'unread' ? 'var(--accent-primary)' : 'var(--text-tertiary)',
              background: readFilter === 'unread' ? 'rgba(35, 131, 226, 0.08)' : 'transparent',
            }}
          >
            Unread
            {readIds.size > 0 ? ` (${visibleItems.filter((i) => !readIds.has(i.id)).length})` : ''}
          </button>
          {displayItems.some((i) => !readIds.has(i.id)) && (
            <button
              onClick={handleMarkAllRead}
              className="px-2 py-1.5 type-caption transition-notion whitespace-nowrap shrink-0"
              style={{ color: 'var(--text-tertiary)', background: 'transparent' }}
              title="Mark all visible articles as read"
            >
              Mark all read
            </button>
          )}
        </div>

        {/* Search bar + Mode toggle */}
        <div className="flex items-center gap-3">
          <div className="relative flex items-center">
            <span
              className="absolute left-2.5 pointer-events-none"
              style={{ color: 'var(--text-tertiary)' }}
            >
              <SearchIcon />
            </span>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (debounceRef.current) clearTimeout(debounceRef.current);
                  const nextQuery = searchInput.trim();
                  setSearchQuery(nextQuery);
                  void executeSearch(nextQuery);
                }
                if (e.key === 'Escape') clearSearch();
              }}
              placeholder="Search..."
              className="notion-input w-full md:w-[240px]"
              style={{
                padding: '5px 10px 5px 28px',
                fontSize: '13px',
              }}
            />
            {isSearchActive && (
              <button
                onClick={clearSearch}
                className="absolute right-1.5 flex items-center justify-center rounded"
                style={{
                  width: '20px',
                  height: '20px',
                  color: 'var(--text-tertiary)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
                title="Clear search"
              >
                &times;
              </button>
            )}
          </div>

          {!isSearchActive && (
            <div
              className="flex rounded-md overflow-hidden"
              style={{ border: '1px solid var(--border-primary)' }}
            >
              {(['top', 'latest'] as FeedMode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className="px-3 py-1 type-label transition-notion capitalize"
                  style={{
                    background: mode === m ? 'var(--accent-primary)' : 'var(--bg-primary)',
                    color: mode === m ? '#fff' : 'var(--text-tertiary)',
                  }}
                >
                  {m === 'top' ? 'Top' : 'Latest'}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Feed */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className={`skeleton rounded-lg ${i === 0 ? 'md:col-span-2' : ''}`}
              style={{ height: i === 0 ? '160px' : '120px' }}
            />
          ))}
        </div>
      ) : displayItems.length === 0 ? (
        <div className="empty-state-panel animate-fade-in">
          <div className="page-eyebrow">{isSearchActive ? 'Search' : 'Ready when you are'}</div>
          <div className="page-title" style={{ fontSize: '30px', margin: 0 }}>
            {isSearchActive ? 'No results found' : 'Your feed is empty'}
          </div>
          <p className="page-subtitle" style={{ textAlign: 'center', maxWidth: '32rem' }}>
            {isSearchActive
              ? 'Try a different query or clear the search to return to your feed.'
              : 'Add a few sources to start the briefing, then use thumbs and your tracking preferences to tune what rises.'}
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {isSearchActive ? (
              <button
                onClick={clearSearch}
                className="notion-btn notion-btn-secondary type-label"
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--border-primary)',
                }}
              >
                Clear search
              </button>
            ) : (
              <>
                <button
                  onClick={() => navigate('/sources')}
                  className="notion-btn notion-btn-primary type-label"
                  style={{ padding: '8px 16px' }}
                >
                  Choose sources
                </button>
                <button
                  onClick={() => navigate('/tracking')}
                  className="notion-btn notion-btn-secondary type-label"
                  style={{
                    padding: '8px 16px',
                    border: '1px solid var(--border-primary)',
                  }}
                >
                  Set tracking
                </button>
              </>
            )}
          </div>
        </div>
      ) : (
        <div>
          {/* Lead story -- full width (RPC handles freshness via briefing stages) */}
          {displayItems[0] && renderTile(displayItems[0], 0, !isSearchActive)}

          {/* Divider between lead and grid */}
          {displayItems.length > 1 && <div className="editorial-divider my-6 md:my-12" />}

          {/* Grid -- 2 columns on desktop */}
          {displayItems.length > 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {displayItems.slice(1, showCount).map((item, i) => renderTile(item, i + 1, false))}
            </div>
          )}

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="h-1" />
        </div>
      )}

      {/* Mark-all-read undo snackbar */}
      {pendingMarkAllRead && (
        <div
          className="fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2.5 rounded-lg animate-slide-up-fade"
          style={{
            background: 'var(--text-primary)',
            color: 'var(--bg-primary)',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          <span className="type-label">
            Marked {pendingMarkAllRead.ids.length} articles as read
          </span>
          <button
            onClick={undoMarkAllRead}
            className="type-label font-semibold"
            style={{
              color: 'var(--accent-secondary)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
            }}
          >
            Undo
          </button>
        </div>
      )}

      {/* Article preview drawer */}
      {previewItem && (
        <ArticleDrawer
          item={previewItem}
          onClose={() => setPreviewItem(null)}
          isPaywalled={paywalledFeeds.has(previewItem.feed_name)}
          onNext={() => {
            const idx = displayItems.findIndex((i) => i.id === previewItem.id);
            const next =
              idx >= 0 && idx < displayItems.length - 1 ? displayItems[idx + 1] : undefined;
            if (next) {
              void handleMarkRead(next.id);
              setPreviewItem(next);
            }
          }}
          onPrev={() => {
            const idx = displayItems.findIndex((i) => i.id === previewItem.id);
            const prev = idx > 0 ? displayItems[idx - 1] : undefined;
            if (prev) {
              void handleMarkRead(prev.id);
              setPreviewItem(prev);
            }
          }}
        />
      )}
    </div>
  );
}

export default FeedPage;
